import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, onAuthStateChanged } from "firebase/auth";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import Alert from 'react-bootstrap/Alert';
import NavBar from '../Components/NavBar';

const LoginPage = () => {
    const [teamName, setTeamName] = useState("");
    const [playerEmail, setPlayerEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [signInSent, setSignInSent] = useState(false);
    const [processingEmailLink, setProcessingEmailLink] = useState(true);
    const navigate = useNavigate();

    // Check if user is already logged in
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Use callable to verify authorization instead of direct Firestore read
                if (!processingEmailLink) {
                    const storedTeam = localStorage.getItem('currentTeam');
                        if (storedTeam) {
                            // If the stored team is the admin marker, redirect to admin review
                            if (storedTeam === 'admin') {
                                navigate('/review-team-requests');
                                return;
                            }

                            try {
                                const verifyTeamEmail = httpsCallable(functions, 'verifyTeamEmail');
                                const result = await verifyTeamEmail({ teamName: storedTeam, email: user.email });
                                if (result.data?.authorized) {
                                    navigate(`/menu/${storedTeam}`);
                                } else {
                                    setErrorMsg('Your email is not authorized for this team.');
                                }
                            } catch (error) {
                                // If callable fails, show a friendly message
                                setErrorMsg(`Unable to verify team authorization: ${error.message || error}`);
                            }
                        }
                }
            }
        });
        return unsubscribe;
    }, [navigate, processingEmailLink]);

    // Handle email link sign-in if returning from email
    useEffect(() => {
        const processEmailLink = async () => {
            try {
                if (isSignInWithEmailLink(auth, window.location.href)) {
                    const email = window.localStorage.getItem('emailForSignIn');
                    const team = window.localStorage.getItem('currentTeam');
                    
                    if (!email || !team) {
                        setErrorMsg("Sign-in information missing. Please request a new sign-in link.");
                        setProcessingEmailLink(false);
                        return;
                    }

                    // Complete the sign-in
                    await signInWithEmailLink(auth, email, window.location.href);
                    
                    // Clear stored data
                    window.localStorage.removeItem('emailForSignIn');
                    
                    // Navigate to menu - the onAuthStateChanged effect above will handle the redirect
                    navigate(`/menu/${team}`);
                } else {
                    // Not an email link URL, safe to show login form
                    setProcessingEmailLink(false);
                    // Clear any leftover email data if the link is invalid
                    window.localStorage.removeItem('emailForSignIn');
                }
            } catch (error) {
                setErrorMsg(`Sign-in failed: ${error.message || 'Please request a new sign-in link.'}`);
                setProcessingEmailLink(false);
                window.localStorage.removeItem('emailForSignIn');
            }
        };

        processEmailLink();
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMsg("");

        try {
            const verifyTeamEmail = httpsCallable(functions, 'verifyTeamEmail');
            const result = await verifyTeamEmail({ teamName, email: playerEmail });

            if (!result.data?.authorized) {
                setErrorMsg("Email not authorized for this team.");
                setIsLoading(false);
                return;
            }

            const actionCodeSettings = {
                url: `${window.location.origin}/?team=${teamName}`,
                handleCodeInApp: true,
            };

            await sendSignInLinkToEmail(auth, playerEmail, actionCodeSettings);

            window.localStorage.setItem('emailForSignIn', playerEmail);
            window.localStorage.setItem('currentTeam', teamName);

            setSignInSent(true);
            setErrorMsg("");
        } catch (error) {
            setErrorMsg(error?.message || "Failed to send sign-in link. Please check your email address.");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <NavBar pageName="Softball Stats Pro" showMenu={false} />
            <Container className='mx-0'>
                <Row>
                    <Col>
                        {processingEmailLink && (
                            <Alert variant="info" className="mt-3">
                                Processing your sign-in... Please wait.
                            </Alert>
                        )}
                        {signInSent && (
                            <Alert variant="success" className="mt-3">
                                Sign-in link sent! Check your email and click the link to sign in.
                            </Alert>
                        )}
                        {errorMsg && (
                            <Alert variant="danger" className="mt-3">
                                {errorMsg}
                                {!processingEmailLink && (
                                    <div className="mt-2">
                                        <Button 
                                            size="sm" 
                                            onClick={() => {
                                                setErrorMsg("");
                                                setSignInSent(false);
                                                setTeamName("");
                                                setPlayerEmail("");
                                            }}
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                )}
                            </Alert>
                        )}
                        {!processingEmailLink && !signInSent && (
                            <Form onSubmit={handleSubmit} aria-label="Log in to Team" className="mt-3">
                                <Form.Group>
                                    <Form.Label htmlFor='team-player-email' visuallyHidden>Player Email</Form.Label>
                                    <InputGroup className='pb-2'>
                                        <InputGroup.Text>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-envelope-at" viewBox="0 0 16 16">
                                                <path d="M2 2a2 2 0 0 0-2 2v8.01A2 2 0 0 0 2 14h5.5a.5.5 0 0 0 0-1H2a1 1 0 0 1-.966-.741l5.64-3.471L8 9.583l7-4.2V8.5a.5.5 0 0 0 1 0V4a2 2 0 0 0-2-2zm3.708 6.208L1 11.105V5.383zM1 4.217V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v.217l-7 4.2z"/>
                                                <path d="M14.247 14.269c1.01 0 1.587-.857 1.587-2.025v-.21C15.834 10.43 14.64 9 12.52 9h-.035C10.42 9 9 10.36 9 12.432v.214C9 14.82 10.438 16 12.358 16h.044c.594 0 1.018-.074 1.237-.175v-.73c-.245.11-.673.18-1.18.18h-.044c-1.334 0-2.571-.788-2.571-2.655v-.157c0-1.657 1.058-2.724 2.64-2.724h.04c1.535 0 2.484 1.05 2.484 2.326v.118c0 .975-.324 1.39-.639 1.39-.232 0-.41-.148-.41-.42v-2.19h-.906v.569h-.03c-.084-.298-.368-.63-.954-.63-.778 0-1.259.555-1.259 1.4v.528c0 .892.49 1.434 1.26 1.434.471 0 .896-.227 1.014-.643h.043c.118.42.617.648 1.12.648m-2.453-1.588v-.227c0-.546.227-.791.573-.791.297 0 .572.192.572.708v.367c0 .573-.253.744-.564.744-.354 0-.581-.215-.581-.8Z"/>
                                            </svg>
                                        </InputGroup.Text>
                                        <Form.Control 
                                            id="team-player-email" 
                                            value={playerEmail} 
                                            type="email" 
                                            onChange={(e) => setPlayerEmail(e.target.value)} 
                                            placeholder='Player Email' 
                                            maxLength={105} 
                                            required
                                            disabled={isLoading}
                                        />
                                    </InputGroup>

                                    <Form.Label htmlFor="team-name" visuallyHidden>Team Name</Form.Label>
                                    <InputGroup className='pb-2'>
                                        <InputGroup.Text>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-person" viewBox="0 0 16 16"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0m4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4m-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10s-3.516.68-4.168 1.332c-.678.678-.83 1.418-.832 1.664z"/></svg>
                                        </InputGroup.Text>
                                        <Form.Control 
                                            id="team-name" 
                                            value={teamName} 
                                            type="text" 
                                            onChange={(e) => setTeamName(e.target.value)} 
                                            placeholder='Team Name' 
                                            maxLength={35} 
                                            required
                                            disabled={isLoading}
                                        />
                                    </InputGroup>

                                    <Button type="submit" className='maxWidth' disabled={isLoading}>
                                        {isLoading ? 'Sending...' : 'Send Sign-In Link'}
                                    </Button>
                                    <div className="mt-2">
                                        <Button variant="link" className="removePadding" onClick={() => navigate('/request-team')}>
                                            Request a new team/player
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Form>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default LoginPage;
